import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Archive, ArrowLeft, DownloadSimple, FileText, Gavel,
  Plus, SealCheck, ShieldCheck, UploadSimple, X,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inputClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const fallbackLabel = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.message || fallback;
};

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    gold: "border-[#C5A059]/40 bg-[#C5A059]/10 text-[#8A692D]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-flex border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-600">
        {label}{required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

function UploadTemplateModal({ meta, saving, onClose, onSave, t, option }) {
  const fileInput = useRef(null);
  const [form, setForm] = useState({
    title: "",
    document_type: "nda",
    language: "pt",
    controlling_language: "pt",
    legal_framework: "portuguese_law_portuguese_courts",
    source: "panatlantik_draft",
    template_version: "1.0",
    jurisdiction: "Portugal",
    governing_law: "Portuguese law",
    license_reference: "",
    lawyer_notes: "",
  });
  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) {
      toast.error(
        t("legalTemplates.selectDocx", "Select a DOCX template first")
      );
      return;
    }
    const payload = new FormData();
    payload.append("file", file);
    Object.entries(form).forEach(([key, value]) =>
      payload.append(key, value)
    );
    onSave(payload);
  };

  const select = (field, values) => (
    <select
      value={form[field]}
      onChange={(event) => set(field, event.target.value)}
      className={inputClass}
    >
      {(values || []).map((value) => (
        <option key={value} value={value}>{option(value)}</option>
      ))}
    </select>
  );

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-950/70 p-4">
      <div className="mx-auto my-8 max-w-4xl bg-[#F8F9FA] shadow-2xl">
        <div className="flex items-center justify-between bg-[#0B2545] px-6 py-5 text-white">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">
              Panatlantik Legal & Compliance
            </p>
            <h2 className="font-serif text-2xl">
              {t("legalTemplates.upload", "Upload controlled template")}
            </h2>
          </div>
          <button type="button" onClick={onClose}><X size={21} /></button>
        </div>
        <form onSubmit={submit} className="space-y-5 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label={t("legalTemplates.fields.title", "Template title")} required>
                <Input
                  value={form.title}
                  onChange={(event) => set("title", event.target.value)}
                  className="h-11 rounded-none"
                  required
                />
              </Field>
            </div>
            <Field label={t("legalTemplates.fields.documentType", "Document type")}>
              {select("document_type", meta?.document_types)}
            </Field>
            <Field label={t("legalTemplates.fields.legalFramework", "Legal framework")}>
              {select("legal_framework", meta?.legal_frameworks)}
            </Field>
            <Field label={t("legalTemplates.fields.language", "Template language")}>
              {select("language", meta?.languages)}
            </Field>
            <Field label={t("legalTemplates.fields.controllingLanguage", "Controlling language")}>
              {select("controlling_language", meta?.languages)}
            </Field>
            <Field label={t("legalTemplates.fields.source", "Template source")}>
              {select("source", meta?.sources)}
            </Field>
            <Field label={t("legalTemplates.fields.version", "Template version")}>
              <Input
                value={form.template_version}
                onChange={(event) => set("template_version", event.target.value)}
                className="h-11 rounded-none"
              />
            </Field>
            <Field label={t("legalTemplates.fields.governingLaw", "Governing law")}>
              <Input
                value={form.governing_law}
                onChange={(event) => set("governing_law", event.target.value)}
                className="h-11 rounded-none"
              />
            </Field>
            <Field label={t("legalTemplates.fields.jurisdiction", "Jurisdiction")}>
              <Input
                value={form.jurisdiction}
                onChange={(event) => set("jurisdiction", event.target.value)}
                className="h-11 rounded-none"
              />
            </Field>
            {form.source === "licensed_third_party" && (
              <div className="md:col-span-2">
                <Field label={t("legalTemplates.fields.licenseReference", "Licence reference")} required>
                  <Input
                    value={form.license_reference}
                    onChange={(event) => set("license_reference", event.target.value)}
                    className="h-11 rounded-none"
                    required
                  />
                </Field>
              </div>
            )}
            <div className="md:col-span-2">
              <Field label={t("legalTemplates.fields.lawyerNotes", "Lawyer/source notes")}>
                <textarea
                  value={form.lawyer_notes}
                  onChange={(event) => set("lawyer_notes", event.target.value)}
                  rows={3}
                  className={`${inputClass} h-auto py-3`}
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label={t("legalTemplates.fields.file", "Approved wording template (DOCX)")} required>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className={inputClass}
                  required
                />
              </Field>
            </div>
          </div>
          <div className="border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">
            {t(
              "legalTemplates.uploadNotice",
              "Upload only wording supplied or approved by a qualified lawyer, or a properly licensed third-party template. Approval makes the source file immutable."
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">
              {t("legalTemplates.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-none bg-[#0B2545] hover:bg-[#12345e]"
            >
              <UploadSimple size={17} className="mr-2" />
              {t("legalTemplates.upload", "Upload controlled template")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApprovalModal({ template, saving, onClose, onApprove, t }) {
  const [form, setForm] = useState({
    reviewer_name: "",
    reviewer_capacity: "",
    reviewer_registration: "",
    notes: "",
    confirm_review: false,
  });
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-950/70 p-4">
      <div className="mx-auto my-8 max-w-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-[#0B2545] px-6 py-5 text-white">
          <div>
            <p className="text-xs text-[#C5A059]">{template.title}</p>
            <h2 className="font-serif text-xl">
              {t("legalTemplates.approveTitle", "Approve legal template")}
            </h2>
          </div>
          <button type="button" onClick={onClose}><X size={21} /></button>
        </div>
        <div className="space-y-4 p-6">
          <Input
            value={form.reviewer_name}
            onChange={(event) => setForm({ ...form, reviewer_name: event.target.value })}
            placeholder={t("legalDocuments.actions.reviewerName")}
            className="h-11 rounded-none"
          />
          <Input
            value={form.reviewer_capacity}
            onChange={(event) => setForm({ ...form, reviewer_capacity: event.target.value })}
            placeholder={t("legalDocuments.actions.reviewerCapacity")}
            className="h-11 rounded-none"
          />
          <Input
            value={form.reviewer_registration}
            onChange={(event) => setForm({ ...form, reviewer_registration: event.target.value })}
            placeholder={t("legalDocuments.actions.reviewerRegistration")}
            className="h-11 rounded-none"
          />
          <textarea
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            placeholder={t("legalDocuments.actions.reviewNotes")}
            rows={4}
            className={`${inputClass} h-auto py-3`}
          />
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.confirm_review}
              onChange={(event) => setForm({ ...form, confirm_review: event.target.checked })}
              className="mt-1"
            />
            {t(
              "legalTemplates.confirmApproval",
              "I confirm that the identified qualified professional reviewed the complete wording and this exact template version."
            )}
          </label>
          <Button
            type="button"
            disabled={saving}
            onClick={() => onApprove(form)}
            className="w-full rounded-none bg-[#0B2545] hover:bg-[#12345e]"
          >
            <Gavel size={17} className="mr-2" />
            {t("legalTemplates.recordApproval", "Approve and lock template")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LegalTemplates() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const [approvalTemplate, setApprovalTemplate] = useState(null);
  const [status, setStatus] = useState("all");

  const option = (value) =>
    t(`legalDocuments.options.${value}`, fallbackLabel(value));

  const { data: meta } = useQuery({
    queryKey: ["legal-template-meta"],
    queryFn: async () => (await api.get("/deal-legal/templates/meta")).data,
  });
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["legal-templates", status],
    queryFn: async () =>
      (
        await api.get("/deal-legal/templates", {
          params: status === "all" ? {} : { status },
        })
      ).data,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["legal-templates"] });

  const uploadMutation = useMutation({
    mutationFn: async (payload) =>
      (
        await api.post("/deal-legal/templates", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data,
    onSuccess: async () => {
      await refresh();
      setShowUpload(false);
      toast.success(t("legalTemplates.uploaded", "Template uploaded"));
    },
    onError: (error) =>
      toast.error(errorMessage(error, t("legalTemplates.actionFailed", "Action failed"))),
  });

  const installMutation = useMutation({
    mutationFn: async () =>
      (await api.post("/deal-legal/templates/install-draft-library")).data,
    onSuccess: async (result) => {
      await refresh();
      toast.success(
        `${result.created_count} ${t("legalTemplates.draftsInstalled", "legal templates activated")}`
      );
    },
    onError: (error) =>
      toast.error(errorMessage(error, t("legalTemplates.actionFailed", "Action failed"))),
  });

  const approvalMutation = useMutation({
    mutationFn: async ({ templateId, payload }) =>
      (
        await api.post(
          `/deal-legal/templates/${templateId}/approve`,
          payload
        )
      ).data,
    onSuccess: async () => {
      await refresh();
      queryClient.invalidateQueries({ queryKey: ["approved-legal-templates"] });
      setApprovalTemplate(null);
      toast.success(t("legalTemplates.approved", "Template approved and locked"));
    },
    onError: (error) =>
      toast.error(errorMessage(error, t("legalTemplates.actionFailed", "Action failed"))),
  });

  const retireMutation = useMutation({
    mutationFn: async (templateId) =>
      (await api.post(`/deal-legal/templates/${templateId}/retire`)).data,
    onSuccess: async () => {
      await refresh();
      queryClient.invalidateQueries({ queryKey: ["approved-legal-templates"] });
      toast.success(t("legalTemplates.retired", "Template retired"));
    },
    onError: (error) =>
      toast.error(errorMessage(error, t("legalTemplates.actionFailed", "Action failed"))),
  });

  const stats = useMemo(() => ({
    total: templates.length,
    draft: templates.filter((item) => item.status === "draft").length,
    approved: templates.filter((item) => item.status === "approved").length,
    retired: templates.filter((item) => item.status === "retired").length,
  }), [templates]);

  const download = async (template) => {
    try {
      const response = await api.get(`/files/${template.file_id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = template.filename || `${template.title}.docx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      toast.error(errorMessage(error, t("legalTemplates.actionFailed", "Action failed")));
    }
  };

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-7 sm:px-7 lg:px-10 lg:py-9">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-5 flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B2545]"
      >
        <ArrowLeft size={17} /> {t("legalTemplates.back", "Back")}
      </button>

      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.24em] text-[#A77E35]">
            Panatlantik Legal & Compliance
          </p>
          <h1 className="font-serif text-3xl text-[#0B2545]">
            {t("legalTemplates.title", "Approved Legal Template Registry")}
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            {t(
              "legalTemplates.subtitle",
              "Control lawyer-approved wording, licences, provenance, versions and immutable source files."
            )}
          </p>
        </div>
        {user?.role === "admin" && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={installMutation.isPending}
              onClick={() => installMutation.mutate()}
              className="h-12 rounded-none border-[#C5A059] px-6 text-[#8A692D]"
            >
              <ShieldCheck size={18} className="mr-2" />
              {t("legalTemplates.installDrafts", "Activate built-in PT/EN documents")}
            </Button>
            <Button
              onClick={() => setShowUpload(true)}
              className="h-12 rounded-none bg-[#0B2545] px-6 hover:bg-[#12345e]"
            >
              <Plus size={18} className="mr-2" />
              {t("legalTemplates.upload", "Upload controlled template")}
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          [FileText, "total", stats.total],
          [ShieldCheck, "draft", stats.draft],
          [SealCheck, "approved", stats.approved],
          [Archive, "retired", stats.retired],
        ].map(([Icon, key, value]) => (
          <div key={key} className="flex items-center gap-4 border border-slate-200 bg-white p-5">
            <Icon size={22} className="text-[#A77E35]" />
            <div>
              <div className="font-serif text-2xl text-[#0B2545]">{value}</div>
              <div className="text-xs text-slate-500">
                {t(`legalTemplates.${key}`, fallbackLabel(key))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-5">
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className={`${inputClass} max-w-xs`}
        >
          <option value="all">{t("legalTemplates.allStatuses", "All statuses")}</option>
          {(meta?.statuses || []).map((value) => (
            <option key={value} value={value}>{option(value)}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-slate-500">
          {t("legalDocuments.loading")}
        </div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500">
          {t("legalTemplates.noTemplates", "No legal templates found.")}
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {templates.map((template) => (
            <article key={template.id} className="border border-slate-200 bg-white">
              <div className="h-1 bg-gradient-to-r from-[#0B2545] via-[#C5A059] to-[#0B2545]" />
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#A77E35]">
                      {option(template.document_type)}
                    </p>
                    <h3 className="mt-1 font-serif text-xl text-[#0B2545]">
                      {template.title}
                    </h3>
                  </div>
                  <Badge tone={
                    template.status === "approved"
                      ? "green"
                      : template.status === "draft"
                        ? "gold"
                        : "slate"
                  }>
                    {option(template.status)}
                  </Badge>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 border-y border-slate-100 py-4 text-xs">
                  <div>
                    <span className="block text-slate-400">{t("legalTemplates.version", "Version")}</span>
                    <span className="text-[#0B2545]">{template.template_version}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">{t("legalTemplates.language", "Language")}</span>
                    <span className="text-[#0B2545]">{option(template.language)}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">{t("legalTemplates.source", "Source")}</span>
                    <span className="text-[#0B2545]">{option(template.source)}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">SHA-256</span>
                    <span className="font-mono text-[#0B2545]">
                      {String(template.sha256 || "").slice(0, 12)}…
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(template.placeholders || []).slice(0, 6).map((placeholder) => (
                    <Badge key={placeholder}>{placeholder}</Badge>
                  ))}
                  {(template.placeholders || []).length > 6 && (
                    <Badge>+{template.placeholders.length - 6}</Badge>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => download(template)}
                    className="h-9 rounded-none text-xs"
                  >
                    <DownloadSimple size={15} className="mr-2" />
                    {t("legalTemplates.download", "Download source")}
                  </Button>
                  {user?.role === "admin" && template.status === "draft" && (
                    <Button
                      type="button"
                      onClick={() => setApprovalTemplate(template)}
                      className="h-9 rounded-none bg-[#C5A059] text-xs text-white hover:bg-[#A77E35]"
                    >
                      <Gavel size={15} className="mr-2" />
                      {t("legalTemplates.approve", "Review and approve")}
                    </Button>
                  )}
                  {user?.role === "admin" && template.status === "approved" && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={retireMutation.isPending}
                      onClick={() => {
                        if (window.confirm(
                          t(
                            "legalTemplates.retireConfirm",
                            "Retire this template? Existing generated records remain unchanged."
                          )
                        )) retireMutation.mutate(template.id);
                      }}
                      className="h-9 rounded-none text-xs text-amber-700"
                    >
                      <Archive size={15} className="mr-2" />
                      {t("legalTemplates.retire", "Retire")}
                    </Button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadTemplateModal
          meta={meta}
          saving={uploadMutation.isPending}
          onClose={() => setShowUpload(false)}
          onSave={(payload) => uploadMutation.mutate(payload)}
          t={t}
          option={option}
        />
      )}
      {approvalTemplate && (
        <ApprovalModal
          template={approvalTemplate}
          saving={approvalMutation.isPending}
          onClose={() => setApprovalTemplate(null)}
          onApprove={(payload) =>
            approvalMutation.mutate({
              templateId: approvalTemplate.id,
              payload,
            })
          }
          t={t}
        />
      )}
    </div>
  );
}
